import { useEffect, useRef, useState } from "react";

import { AsyncTaskStatusUpdateEvent, AsyncTaskStatusUpdateEventType } from "@performyard/service-contracts";

import { getHeaders, getUrl } from "../../api/services/defs/internal-api/internal-base-request";

type UseAsyncTaskStreamReturn = {
	task: AsyncTaskStatusUpdateEventType | null;
	isConnected: boolean;
	error: Error | null;
	isRetrying: boolean;
};

const MAX_RETRY_ATTEMPTS = 5;
const RETRY_DELAYS = [1000, 2000, 4000, 8000, 16000]; // Exponential backoff in milliseconds

/**
 * Hook to stream async task status updates via Server-Sent Events (SSE).
 * Automatically connects on mount, handles retries with exponential backoff,
 * and provides type-safe task updates.
 *
 * @param taskId - The ID of the async task to monitor (null or empty string to skip connection)
 * @returns Object containing task data, connection status, error state, and retry status
 *
 * @example
 * const { task, isConnected, error, isRetrying } = useAsyncTaskStream(taskId);
 *
 * if (error) {
 *   return <div>Error: {error.message}</div>;
 * }
 *
 * if (!task) {
 *   return <div>Loading...</div>;
 * }
 *
 * const progress = task.progressTotal > 0 ? (task.progressCount / task.progressTotal) * 100 : 0;
 *
 * return (
 *   <div>
 *     <p>Status: {task.status}</p>
 *     <p>Progress: {progress}%</p>
 *   </div>
 * );
 */
export const useAsyncTaskStream = (taskId: string | null): UseAsyncTaskStreamReturn => {
	const [task, setTask] = useState<AsyncTaskStatusUpdateEventType | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const [isRetrying, setIsRetrying] = useState(false);

	const abortControllerRef = useRef<AbortController | null>(null);
	const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const retryCountRef = useRef(0);
	const isMountedRef = useRef(true);

	useEffect(() => {
		isMountedRef.current = true;

		if (!taskId) {
			setTask(null);
			setError(null);
			setIsRetrying(false);
			retryCountRef.current = 0;
			return;
		}

		const connectToStream = async (): Promise<void> => {
			if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
				setError(new Error("Max retry attempts reached"));
				setIsRetrying(false);
				return;
			}

			// Create new abort controller for this connection attempt
			abortControllerRef.current = new AbortController();

			try {
				const url = getUrl(`/v1/async-tasks/${taskId}/stream`);
				const headers = getHeaders();

				const response = await fetch(url, {
					headers: headers as Record<string, string>,
					signal: abortControllerRef.current.signal,
				});

				if (!response.ok) {
					throw new Error(`Failed to connect to stream: ${response.statusText}`);
				}

				if (!response.body) {
					throw new Error("Response body is null");
				}

				// Connection successful - reset retry count and update state
				retryCountRef.current = 0;
				setIsConnected(true);
				setIsRetrying(false);
				setError(null);

				// Read the stream
				const reader = response.body.getReader();
				const decoder = new TextDecoder();
				let buffer = "";

				while (isMountedRef.current) {
					const { done, value } = await reader.read();

					if (done) {
						break;
					}

					// Decode the chunk and add to buffer
					buffer += decoder.decode(value, { stream: true });

					// Process complete SSE messages (separated by double newlines)
					const lines = buffer.split("\n\n");
					buffer = lines.pop() || ""; // Keep incomplete message in buffer

					for (const line of lines) {
						if (line.startsWith("data:")) {
							const data = line.substring(5).trim(); // Remove "data:" prefix

							try {
								const parsed = JSON.parse(data);
								const validated = AsyncTaskStatusUpdateEvent.parse(parsed);
								setTask(validated);

								if (validated.status === "completed" || validated.status === "failed") {
									reader.cancel();
									setIsConnected(false);
									return;
								}
							} catch (parseError) {
								// Continue processing other messages
							}
						}
					}
				}

				setIsConnected(false);
			} catch (err) {
				// Don't treat abort as an error
				if (err instanceof Error && err.name === "AbortError") {
					return;
				}

				// Only update state if component is still mounted
				if (!isMountedRef.current) return;

				const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
				setError(new Error(errorMessage));
				setIsConnected(false);

				// Schedule retry with exponential backoff
				const retryDelay = RETRY_DELAYS[Math.min(retryCountRef.current, RETRY_DELAYS.length - 1)];
				retryCountRef.current += 1;
				setIsRetrying(true);

				retryTimeoutRef.current = setTimeout(() => {
					if (isMountedRef.current) {
						connectToStream();
					}
				}, retryDelay);
			}
		};

		// Start the connection
		connectToStream();

		// Cleanup function
		return () => {
			isMountedRef.current = false;

			// Cancel any in-flight request
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			// Clear any pending retry
			if (retryTimeoutRef.current) {
				clearTimeout(retryTimeoutRef.current);
			}

			setIsConnected(false);
		};
	}, [taskId]);

	return {
		task,
		isConnected,
		error,
		isRetrying,
	};
};
